import React, { useEffect, useMemo, useRef, useState } from 'react';

import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import { Box, Grid, Typography } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { makeStyles } from '@mui/styles';
import { Button } from '@repo/videa-components/src/index';
import classNames from 'classnames';
import { useAtom, useAtomValue } from 'jotai';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
// @ts-expect-error TS(7016): Could not find a declaration file for module 'reac... Remove this comment to see the full error message
import useKeypress from 'react-use-keypress';

import { selectTeethAtom } from '@/modules/patientList/atoms';

import { selectedVisitAtom } from '../../../app/appHeader/imageViewerMenu/atoms';
import { addMessage } from '../../../app/snackbar/SnackbarActions';
import { usePreferences } from '../../../react-query/usePreferences';
import {
  generateUserModificationsRequest,
  useUpdateUserToolbarSettings,
} from '../../../react-query/useUpdateUserModifications';
import {
  getExpectedSlotsForMissingImage,
  getImageFindingsFromImageQuality,
} from '../../../utils/claimSummary/imageFindings';
import { isValidToothNumber } from '../../../utils/finding';
import {
  useDebouncedEffect,
  useRestrictClinicalInteractionsForDirectors,
} from '../../../utils/hooks';
import { mixpanelEvents, useTrackEvent } from '../../../utils/mixpanel';
import AnnotatorConstants from '../annotator.constants';
import { saveUserModificationsAction } from '../AnnotatorActions';
import { imageQualityAtom, showPastVisitsAtom } from '../atoms';
import { disableKeyPressAtom } from '../casePresentation/atoms';
import { Compare } from '../compare/Compare';
import { multiFileViewerLog, trackRender } from '../debug-logger';
import { ImageToolbar } from '../imageToolbar/ImageToolbar';
import type { ImageObject } from '../types';
import { ALL_KEYS, KEY_MAP } from '../utils';

import FindingViewer from './findingViewer/FindingViewer';
import { FullScreenImageViewer } from './findingViewer/FullScreenImageViewer';
import { getModelErrorMessage } from './findingViewer/utils';
import {
  brightnessValueAtom,
  contrastValueAtom,
  enabledHDAtom,
  isOpacityInvertedAtom,
  shouldPersistUserModificationsAtom,
  showImageQualityFindingsAtom,
} from './imageToolbar/atoms';
import { ImageViewerFooter } from './ImageViewerFooter/ImageViewerFooter';
import { LayoutViewer } from './LayoutViewer';
import { ModelErrorIcon } from './ModelErrorIcon';
import type { MultiFileViewerProps } from './MultiFileViewer.types';
import { useRotation } from './RotationContext';
import { calculateImageRows } from './utils';

const localStyles = makeStyles((theme) => ({
  container: {
    background: 'black',
    position: 'relative',
    height: '100%',
    justifyContent: 'space-between',
    minHeight: '34em',
    minWidth: '52em',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',

    '& .viewer-header': {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: () => 'flex-start',
      padding: '0 1em',
      '& .header-left': {
        display: 'flex',
        flexDirection: 'row',
        gap: '1em',
        alignItems: 'center',
      },
    },
    '& .present-findings': {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: '16px',
    },

    '& .viewer-layout': {
      display: 'flex',
      flexDirection: 'column',
      boxSizing: 'border-box',
      height: '100%',
      width: '100%',
      margin: '0',
      position: 'relative',
      overflow: 'hidden',
      flex: 1,
      minHeight: 0,
      minWidth: 0,

      '&.viewer-odontogram': {
        position: 'relative',
        height: 'calc(100% - 4.25em)',
        '& .image-controls-container': {
          zIndex: 100,
          position: 'absolute',
          bottom: '0',
          left: '0',
          '& .image-controls': {
            display: 'none',
          },
        },
      },
    },
    '& .viewer-compare': {
      height: '100%',
      width: '100%',
      paddingTop: '0',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '1em',
    },
    '& .viewer-compare-sidebar': {
      height: '100%',
      width: '100%',
      paddingTop: '0',
      position: 'relative',
      margin: '0',
    },
    '@media (max-height: 768px)': {
      minHeight: `344px`,
      minWidth: '100%',
      justifyContent: 'flex-start',
    },
    '@media (max-width: 800px)': {
      minHeight: `400px`,
      minWidth: '100%',
      justifyContent: 'flex-start',
    },
  },
  strip: {
    display: 'flex',
    overflowY: 'hidden',
    overflowX: 'auto',
    width: '100vw',
    height: '13em',
    minHeight: '13em',
    padding: '2em 0 3em 3em',
    gap: '1.75em',
    boxSizing: 'border-box',
    position: 'relative',
    zIndex: 100,

    '.viewer-odontogram &': {
      position: 'absolute',
      bottom: '6em',
      left: '0px',
    },
  },
  grid: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    height: '100%',
    paddingLeft: '4em',

    '&.redesign-enabled': {
      justifyContent: 'flex-start',
      alignItems: 'center',
    },
  },
  imageContainer: {
    height: 'calc(100% - 3.5em)',
  },
  findingViewerContainer: {
    '&:hover': {
      opacity: 0.4,
      cursor: 'pointer',
    },
  },
  rerunLoadingContainer: {
    background: 'black',
    opacity: 0.8,
    left: 0,
    top: 0,
    zIndex: 100,
  },
  findingViewerContainerNoHover: {
    '&:hover': {
      opacity: 0.4,
      cursor: 'pointer',
    },
  },
  bottomActionContainer: {
    position: 'absolute',
    width: '300px',
    bottom: theme.spacing(0.5),
    right: theme.spacing(2.5),
  },
  viewerLabelContainer: {
    bottom: '5px',
    left: '5px',
    position: 'absolute',
    zIndex: 10,
  },
  disclaimer: {
    color: 'white',
    fontStyle: 'italic',
    fontSize: '12px',
  },
  tutorialAnchorRef: {
    position: 'absolute',
    left: 0,
    top: 0,
    width: 10,
    height: 10,
  },
  hoveredItem: {
    cursor: 'pointer',
  },
  toothIcon: {
    zIndex: 5,
    marginRight: '10px',
  },
  toothIconRow: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    height: '100%',
    alignItems: 'center',
    position: 'relative',
  },
  mlMessage: {
    fontSize: '0.6rem',
    marginLeft: 35,
  },
}));

const MultiFileViewer: React.FC<MultiFileViewerProps> = (props) => {
  const {
    activeImage,
    isUsingLatestModel,
    addAnnotation,
    deleteUserAnnotation,
    annotationDrawType,
    annotatorAnchorEls,
    changeViewerMode,
    currentImageFilter,
    currentlyHoveredImageId,
    currentPatient,
    enabledFindingTypes,
    onExpandSidebar,
    hasHoveredFinding,
    onUpdateOdontogramView,
    imageKeysToDisplay,
    isFetchingLatestImages,
    isSalesDemo,
    numberOfSortedFMXImages,
    numberOfBitewings,
    patientVisitsData,
    shouldUseSeriesView = false,
    onSetCurrentImageFilter,
    onSetCurrentImageIndex,
    onSetCurrentlyHoveredImageId,
    onSetEnabledFindingTypes,
    shouldDisplayRootTips,
    sidebarIsNotCollapsed,
    onChangeViewerMode,
    sortedAndFilteredImages,
    trackCornerstoneMouseEvent,
    trackImageInversion,
    updateFinding,
    updateToothNumber,
    user,
    viewerMode,
    visitSummary,
    onLeaveImage,
    fmxIndexesByType,
    scheduledTeeth,
  } = props;

  trackRender(multiFileViewerLog, 'MultiFileViewer', {
    activeImageId: activeImage?.objectId,
    currentImageFilter,
    imageCount: sortedAndFilteredImages?.length,
    sidebarIsNotCollapsed,
  });

  const [isOpacityInverted, setIsOpacityInverted] = useAtom(
    isOpacityInvertedAtom,
  );
  const [isTutorialVisible, setIsTutorialVisible] = useState(false);
  const [magnifyAnchorEl, setMagnifyAnchorEl] = useState(null);
  const [arrowAnchorEl] = useState(null);
  const tutorialAnchorRef = useRef();
  const [modifiedImageId, setModifiedImageId] = useState(null);
  const [contrastValue, setContrastValue] = useAtom(contrastValueAtom);
  const [brightnessValue, setBrightnessValue] = useAtom(brightnessValueAtom);
  const [shouldPersistUserModifications, setShouldPersistUserModifications] =
    useAtom(shouldPersistUserModificationsAtom);
  const { data } = usePreferences();
  const isHQFlagDisabled = Boolean(data?.hqOffByDefault);
  const [showPastVisits, setShowPastVisits] = useAtom(showPastVisitsAtom);
  const selectedVisitId = useAtomValue(selectedVisitAtom);
  const selectedTeeth = useAtomValue(selectTeethAtom);
  const shouldDisableKeyPress = useAtomValue(disableKeyPressAtom);
  const imageQuality = useAtomValue(imageQualityAtom);
  const showImageQualityFindings = useAtomValue(showImageQualityFindingsAtom);
  const [enabledHD, setEnabledHD] = useAtom(enabledHDAtom);

  const { id: selectedPatientId, imageId } = useParams<{
    id: string;
    imageId?: string;
  }>();

  const { data: prefs } = usePreferences();
  const isRestrictClinicalInteractions =
    useRestrictClinicalInteractionsForDirectors();

  const classes = localStyles();

  const dispatch = useDispatch();

  const { trackEvent } = useTrackEvent();

  const {
    isRotationButtonHovered,
    rotateImage,
    isLoadingRerun,
    rotatedImages,
    setIsOnSingleImageView,
  } = useRotation();

  const { t } = useLingui();

  const isOnMultiFileViewer = !imageId;

  // Get image findings for the current single image view
  const currentImageFindings = useMemo(() => {
    multiFileViewerLog.log('memo: currentImageFindings recomputed', {
      isOnMultiFileViewer,
      imageManifestId: activeImage?.imageManifestId,
      showImageQualityFindings,
    });
    if (
      isOnMultiFileViewer ||
      !activeImage?.imageManifestId ||
      !prefs?.imageQualityInsightsInImageViewer ||
      !showImageQualityFindings
    ) {
      return [];
    }
    return getImageFindingsFromImageQuality(
      activeImage.imageManifestId,
      imageQuality,
      scheduledTeeth,
    );
  }, [
    isOnMultiFileViewer,
    activeImage?.imageManifestId,
    imageQuality,
    prefs?.imageQualityInsightsInImageViewer,
    showImageQualityFindings,
    scheduledTeeth,
  ]);

  const handleToggleHQEnabled = () => {
    if (prefs?.adjustmentVersionHD2) {
      setEnabledHD(!enabledHD ? 'HD1' : false);
      setShouldPersistUserModifications(true);
      trackEvent(mixpanelEvents.hdVersionSelected, {
        enabledHD: !enabledHD ? 'HD1' : undefined,
        source: `${
          isOnMultiFileViewer ? 'multiFileViewer' : 'singleImageViewer'
        } - keys`,
      });
      return;
    }
    setEnabledHD(enabledHD ? false : 'HD1');
    setShouldPersistUserModifications(true);
    trackEvent(mixpanelEvents.hqToggle, {
      enabledHD: enabledHD ? undefined : 'HD1',
      source: `${
        isOnMultiFileViewer ? 'multiFileViewer' : 'singleImageViewer'
      } - keys`,
    });
  };
  /* eslint-disable-next-line react-hooks/exhaustive-deps -- The 'imagesArray' conditional could make the dependencies of useMemo Hook (at line 339) change on every render. To fix this, wrap the initialization of 'imagesArray' in its own useMemo() Hook. */
  const imagesArray = currentPatient?.images
    ? Object.values(currentPatient?.images)
    : [];

  const currentImageIndex = imageKeysToDisplay.findIndex(
    (currentImageId: any) =>
      activeImage && currentImageId === activeImage?.objectId,
  );

  // update current image list for nav bar
  useEffect(() => {
    multiFileViewerLog.log('effect: currentImageIndex changed', {
      currentImageIndex,
    });
    onSetCurrentImageIndex(currentImageIndex);
  }, [currentImageIndex, onSetCurrentImageIndex]);

  useEffect(() => {
    // TODO: This is a hack to make sure that all the images resize properly when the view changes between FMX and BW
    // I couldn't figure out the reason why the images aren't resizing properly for certain screen sizes in the time available
    multiFileViewerLog.log(
      'effect: currentImageFilter changed, dispatching resize',
      { currentImageFilter },
    );
    window.dispatchEvent(new Event('resize'));
  }, [currentImageFilter]);

  useEffect(() => {
    multiFileViewerLog.log(
      'effect: activeImage changed → setIsOnSingleImageView',
      {
        hasModels: !!activeImage?.models,
        objectId: activeImage?.objectId,
      },
    );
    setIsOnSingleImageView(!!activeImage?.models);
  }, [activeImage, setIsOnSingleImageView]);

  const toggleOpacity = () => {
    // This sets Annotator's local state prop and updates the FindingViewer
    trackImageInversion(activeImage, !isOpacityInverted, user);
    setShouldPersistUserModifications(true);
    setIsOpacityInverted((prev) => !prev);
  };

  const magnifyAnchorRef = (el: any) => setMagnifyAnchorEl(el);
  const tutorialAnchorElements = {
    magnify: magnifyAnchorEl,
    slider: annotatorAnchorEls.slider,
    tutorialAnchor: tutorialAnchorRef.current,
    agree: annotatorAnchorEls.agree,
    watch: annotatorAnchorEls.watch,
    disagree: annotatorAnchorEls.disagree,
    arrow: arrowAnchorEl,
  };

  const modelErrorMessage = getModelErrorMessage(activeImage);

  const noImagesOnCurrentView = useMemo(
    () => sortedAndFilteredImages.every((img: any) => !img.objectId),
    [sortedAndFilteredImages],
  );

  const noImages = useMemo(
    () =>
      noImagesOnCurrentView &&
      imagesArray.filter((i) => i.imageGroup === 'OTHER').length === 0,
    [noImagesOnCurrentView, imagesArray],
  );

  const expectedSlotsForMissingImage = useMemo(() => {
    if (
      currentImageFilter !== AnnotatorConstants.FMX ||
      !scheduledTeeth?.size ||
      !sortedAndFilteredImages?.length
    ) {
      return null;
    }
    const slotsWithImages = new Set<number>();
    for (const img of sortedAndFilteredImages) {
      const slot = img.fmxIndex;
      if (
        img.imageManifestId != null &&
        slot != null &&
        slot >= 0 &&
        slot <= 17
      ) {
        slotsWithImages.add(slot);
      }
    }
    return getExpectedSlotsForMissingImage(scheduledTeeth, slotsWithImages);
  }, [currentImageFilter, scheduledTeeth, sortedAndFilteredImages]);

  const noImagesMessageToDisplay = (imageFilter: any) => {
    if (imageFilter === AnnotatorConstants.BITEWING) {
      return t`No bitewing images found for this patient`;
    }
    if (imageFilter === AnnotatorConstants.FMX) {
      return t`No bitewings or periapical images found for this patient`;
    }
    if (imageFilter === AnnotatorConstants.OTHER) {
      return t`No additional images found for this patient`;
    }
  };

  const userModifications = useUpdateUserToolbarSettings();

  // if any of the toolbar buttons are interacted with, wait 5 seconds before triggering a request
  useDebouncedEffect(
    () => {
      multiFileViewerLog.log(
        'debouncedEffect: persist user modifications triggered',
        {
          brightnessValue,
          contrastValue,
          isOpacityInverted,
          enabledHD,
          modifiedImageId,
          shouldPersistUserModifications,
          activeImageId: activeImage?.objectId,
        },
      );
      if (activeImage) {
        // enables triggering the persistance request even if the user navigates back to the fmx view
        // @ts-expect-error TS(2345): Argument of type 'string' is not assignable to par... Remove this comment to see the full error message
        setModifiedImageId(activeImage?.objectId);
      }
      const patientId = currentPatient.id || currentPatient.patientId;

      const modifiedImage: ImageObject = sortedAndFilteredImages.find(
        (image: ImageObject) => image.imageId === modifiedImageId,
      );
      // Do not persist dental photo changes, they currently cause a 403
      if (modifiedImage?.detectedImageType === 'COLOR_IMAGE') {
        setShouldPersistUserModifications(false);
        return;
      }
      if (modifiedImageId && shouldPersistUserModifications) {
        const userModificationRequest = generateUserModificationsRequest(
          modifiedImageId,
          brightnessValue,
          contrastValue,
          isOpacityInverted,
          enabledHD === undefined ? !isHQFlagDisabled : !!enabledHD,
          activeImage?.analysisId,
        );

        if (!activeImage) {
          setShouldPersistUserModifications(false);
          return;
        }

        if (isSalesDemo || isRestrictClinicalInteractions) {
          dispatch(
            saveUserModificationsAction(
              patientId,
              modifiedImageId,
              userModificationRequest.userModificationsBody,
            ),
          );
          setShouldPersistUserModifications(false);
        } else {
          userModifications.mutate(userModificationRequest, {
            onError: () =>
              dispatch(
                addMessage(
                  t`Sorry, we could not save your changes. Please try again later`,
                  'error',
                  2000,
                ),
              ),
            onSettled: () => {
              dispatch(
                saveUserModificationsAction(
                  patientId,
                  modifiedImageId,
                  userModificationRequest.userModificationsBody,
                ),
              );
              setShouldPersistUserModifications(false);
            },
          });
        }
      }
    },
    500,
    [
      brightnessValue,
      contrastValue,
      isOpacityInverted,
      enabledHD,
      modifiedImageId,
    ],
  );

  useKeypress(ALL_KEYS, (event: { key: any }) => {
    if (shouldDisableKeyPress) return;
    switch (true) {
      case KEY_MAP.keyPressedInvert.includes(event.key):
        toggleOpacity();
        break;
      case KEY_MAP.keyPressedSharpen.includes(event.key):
        handleToggleHQEnabled();
        break;
      case KEY_MAP.keyPressedWindowLevel1.includes(event.key):
        setBrightnessValue(0);
        break;
      case KEY_MAP.keyPressedWindowLevel2.includes(event.key):
        setBrightnessValue(50);
        break;
      case KEY_MAP.keyPressedWindowLevel3.includes(event.key):
        setBrightnessValue(100);
        break;
      case KEY_MAP.keyPressedWindowLevel4.includes(event.key):
        setBrightnessValue(150);
        break;
      case KEY_MAP.keyPressedWindowLevel5.includes(event.key):
        setBrightnessValue(200);
        break;
      case KEY_MAP.keyPressedWindowLevel6.includes(event.key):
        setBrightnessValue(250);
        break;
      case KEY_MAP.keyPressedWindowLevel0.includes(event.key):
        setBrightnessValue(128);
        break;
      case KEY_MAP.keyPressedRotate.includes(event.key):
        rotateImage(activeImage);
        break;
    }
  });

  const { imagesPerRow, numberOfRows } = calculateImageRows(
    currentImageFilter,
    sortedAndFilteredImages.length,
  );

  const viewerLayoutProps = {
    brightnessValue,
    contrastValue,
    currentImageFilter,
    currentlyHoveredImageId,
    currentPatient,
    enabledFindingTypes,
    imagesPerRow,
    enabledHD,
    isHQFlagDisabled,
    isOpacityInverted,
    isRotationButtonHovered,
    noImagesOnCurrentView,
    numberOfRows,
    imageCount: sortedAndFilteredImages.length,
    numberOfSortedFMXImages,
    sidebarIsNotCollapsed,
    sortedAndFilteredImages,
    updateToothNumber,
    user,
    viewerMode,
    onSetCurrentlyHoveredImageId,
    onExpandSidebar,
    onUpdateOdontogramView,
    onSetBrightnessValue: setBrightnessValue,
    onSetContrastValue: setContrastValue,
    onSetCurrentImageFilter,
    shouldUseSeriesView,
    onSetEnabledHD: setEnabledHD,
    onSetIsOpacityInverted: setIsOpacityInverted,
    onSetShouldPersistUserModifications: setShouldPersistUserModifications,
    setIsOnSingleImageView,
    scheduledTeeth,
    expectedSlotsForMissingImage,
  };

  // Calculate the last analysis date and pass to ImageToolbar
  const firstImageWithUpdatedAt = sortedAndFilteredImages.find(
    (image: any) => !!image.updatedAt,
  );
  let lastAnalysisDate = undefined;
  if (firstImageWithUpdatedAt) {
    lastAnalysisDate = new Date(firstImageWithUpdatedAt.updatedAt);
  }

  const findingViewer = (
    <>
      {(isLoadingRerun || isFetchingLatestImages) && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
          height="100%"
          width="100%"
          position="absolute"
          className={classes.rerunLoadingContainer}
        >
          {isLoadingRerun && (
            <Typography
              style={{ color: 'white', fontWeight: 100, marginBottom: 20 }}
              variant="h5"
            >
              <Trans>Analyzing images, please wait...</Trans>
            </Typography>
          )}

          <CircularProgress />
        </Box>
      )}
      {modelErrorMessage && (
        <ModelErrorIcon
          variant="regular"
          modelErrorMessage={modelErrorMessage}
        />
      )}
      {activeImage && activeImage?.models && !showPastVisits ? (
        <FindingViewer
          activeImage={activeImage}
          addAnnotation={addAnnotation}
          deleteUserAnnotation={deleteUserAnnotation}
          annotationDrawType={annotationDrawType}
          annotatorAnchorEls={tutorialAnchorElements}
          hasHoveredFinding={hasHoveredFinding}
          imageFindings={currentImageFindings}
          isThumbnail={false}
          isTutorialVisible={isTutorialVisible}
          key={activeImage?.objectId}
          patient={currentPatient}
          setIsTutorialVisible={setIsTutorialVisible}
          shouldDisplayRootTips={shouldDisplayRootTips}
          trackCornerstoneMouseEvent={trackCornerstoneMouseEvent}
          updateFinding={updateFinding}
          changeViewerMode={changeViewerMode}
          // @ts-expect-error TS(2783): 'updateToothNumber' is specified more than once, s... Remove this comment to see the full error message
          updateToothNumber={updateToothNumber}
          // @ts-expect-error TS(2783): 'user' is specified more than once, so this usage ... Remove this comment to see the full error message
          user={user}
          {...viewerLayoutProps}
        />
      ) : !isFetchingLatestImages && noImages ? (
        <div
          style={{
            height: '100%',
            textAlign: 'center',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <h1 style={{ color: 'white', fontWeight: 100 }}>
            {noImagesMessageToDisplay(currentImageFilter)}
          </h1>
        </div>
      ) : (
        <LayoutViewer classes={classes} isThumbnail {...viewerLayoutProps} />
      )}
    </>
  );

  const isCurrentImageFilterATooth = isValidToothNumber(currentImageFilter);
  const viewerLayoutClasses = classNames({
    'viewer-layout': true,
    'viewer-compare-sidebar': showPastVisits,
    'viewer-odontogram':
      currentImageFilter === AnnotatorConstants.ODONTOGRAM ||
      isCurrentImageFilterATooth,
  });

  return (
    <Grid container className={classes.container}>
      {/** @ts-expect-error TS(2322): Type 'MutableRefObject<undefined>' is not assignab... Remove this comment to see the full error message */}
      <div className={classes.tutorialAnchorRef} ref={tutorialAnchorRef} />
      <div className={viewerLayoutClasses}>
        {(!!activeImage || !!selectedTeeth.length) && !showPastVisits && (
          <Button
            data-testid="back-to-fmx-button"
            variant="ghost"
            className="vai-self-start vai-flex vai-items-center vai-gap-2 vai-text-zinc-50 hover:vai-text-zinc-50 vai-px-3 vai-py-2 vai-my-3 vai-ml-16 hover:vai-bg-zinc-700"
            onClick={onLeaveImage}
          >
            <FontAwesomeIcon icon={faChevronLeft} className="vai-w-5 vai-h-5" />
            <p className="vai-font-inter">
              {(() => {
                if (currentImageFilter === AnnotatorConstants.BITEWING) {
                  return shouldUseSeriesView ? (
                    <Trans>Back to Series</Trans>
                  ) : (
                    <Trans>Back to BW</Trans>
                  );
                }
                return <Trans>Back to FMX</Trans>;
              })()}
            </p>
          </Button>
        )}

        <ImageToolbar
          activeImage={activeImage}
          onChangeViewerMode={onChangeViewerMode}
          onSetShouldPersistUserModifications={
            setShouldPersistUserModifications
          }
        />
        {!showPastVisits ? findingViewer : showPastVisits && <Compare />}
      </div>
      {/* Footer always at the bottom, outside scrollable area */}
      {!!findingViewer && !showPastVisits && (
        <ImageViewerFooter
          activeImage={activeImage}
          numberOfBitewings={numberOfBitewings}
          lastAnalysisDate={lastAnalysisDate}
          visitSummary={visitSummary}
          fmxIndexesByType={fmxIndexesByType}
          {...viewerLayoutProps}
        />
      )}
      {activeImage && <FullScreenImageViewer activeImage={activeImage} />}
    </Grid>
  );
};

MultiFileViewer.defaultProps = {
  activeImage: undefined,
  addAnnotation: () => {},
  annotationDrawType: 'caries',
  annotatorAnchorEls: {},
  appointment: {},
  confidenceThreshold: 50,
  confidenceThresholds: {},
  currentImageFilter: '',
  enabledFindingTypes: {},
  hasHoveredFinding: false,
  sortedAndFilteredImages: [],
  imageKeysToDisplay: [],
  onSetCurrentImageFilter: () => {},
  changeViewerMode: () => {},
  updateFinding: () => {},
  user: {},
  viewerMode: 0,
  updateToothNumber: () => {},
  setCurrentImageIndex: () => {},
  setCurrentlyHoveredImageId: () => {},
  currentlyHoveredImageId: '',
  setEnabledFindingTypes: () => {},
  sidebarIsNotCollapsed: true,
  shouldDisplayRootTips: true,
  // @ts-expect-error TS(2740): Type '{}' is missing the following properties from... Remove this comment to see the full error message
  currentPatient: {},
  isFetchingLatestImages: false,
  numberOfBitewings: 0,
  isSalesDemo: false,
};

export default MultiFileViewer;
